import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { endpoints } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { useApiClient } from '../../../lib/apiClient';
import { notify } from '../../toaster';
import { RULES_QUERY_KEY } from './rulesQueryKeys';

type MoveRuleParams = {
  rule: QueueRule;
  targetQueue: Queue;
};

export const hasMoveToAnotherQueueAction = (
  _queue: Queue | undefined,
  rule: QueueRule
) => rule.queues.length === 1;

export const useMoveRule = (onSuccess?: () => void) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const intl = useIntl();

  return useMutation({
    mutationFn: async ({ rule, targetQueue }: MoveRuleParams) => {
      // Validate that the rule can be moved - must have exactly one queue
      if (rule.queues.length !== 1) {
        throw new Error('Cannot move rule: rule must have exactly one queue');
      }

      const patchedRule: Partial<QueueRule> = {
        queues: [targetQueue.url],
      };

      return api.request(endpoints.rules.patch(rule.id, patchedRule));
    },
    onSuccess: (movedRule, { targetQueue }) => {
      queryClient.invalidateQueries([RULES_QUERY_KEY]);
      onSuccess?.();

      const toastId = `move-rule-${movedRule.id}`;
      notify.default({
        title: intl.formatMessage(
          {
            id: 'features.queueSettings.rules.list.move.notification.success',
          },
          {
            queueName: targetQueue.name,
          }
        ),
        toastActions: targetQueue.id
          ? [
              {
                to: `/queues/${targetQueue.id}/settings/rules/${movedRule.id}/detail`,
                onClick: () => {
                  toast.dismiss(toastId);
                },
                label: intl.formatMessage({
                  id: 'components.toaster.actions.goToQueue',
                }),
              },
            ]
          : undefined,
        id: toastId,
      });
    },
    onError: () => {
      notify.error({
        title: intl.formatMessage({
          id: 'features.queueSettings.rules.list.move.notification.failure',
        }),
      });
    },
  });
};
